import React, { useState, useEffect } from 'react';
import { Database, Server, Activity, CheckCircle, AlertCircle, Users, Package, Building, ArrowRightLeft, Clock, TrendingUp } from 'lucide-react';
import ViewLayout from '../ui/ViewLayout';

const DatabaseDashboardView = ({ 
  props, 
  organizations, 
  transferRequests, 
  userLists, 
  reservations = [],
  setActiveView
}) => {
  const [systemStats, setSystemStats] = useState({
    totalProps: 0,
    availableProps: 0,
    checkedOutProps: 0,
    reservedProps: 0,
    totalOrganizations: 0,
    activeTransfers: 0,
    totalLists: 0,
    pendingReservations: 0
  });

  useEffect(() => {
    // Calculate system statistics
    const totalProps = props.length;
    const availableProps = props.filter(p => p.status === 'available').length;
    const checkedOutProps = props.filter(p => p.status === 'checked_out').length;
    const reservedProps = props.filter(p => p.status === 'reserved').length;
    const totalOrganizations = organizations.length;
    const activeTransfers = transferRequests.filter(t => t.status === 'pending').length;
    const totalLists = userLists?.length || 0;
    const pendingReservations = reservations.filter(r => r.status === 'pending').length;

    setSystemStats({
      totalProps,
      availableProps,
      checkedOutProps,
      reservedProps,
      totalOrganizations,
      activeTransfers,
      totalLists,
      pendingReservations
    });
  }, [props, organizations, transferRequests, userLists, reservations]);

  // Calculate system health score
  const calculateHealthScore = () => {
    const availabilityRate = systemStats.totalProps > 0 ? (systemStats.availableProps / systemStats.totalProps) * 100 : 0;
    const utilizationRate = systemStats.totalProps > 0 ? (systemStats.checkedOutProps / systemStats.totalProps) * 100 : 0;
    
    // Ideal system: 60-80% available, 20-40% in use
    let score = 100;
    if (availabilityRate < 40) score -= 30; // Too few available
    if (availabilityRate > 90) score -= 20; // Too many unused
    if (utilizationRate < 10) score -= 20; // Underutilized
    if (utilizationRate > 60) score -= 25; // Overutilized
    
    return Math.max(0, Math.min(100, score));
  };

  const healthScore = calculateHealthScore();

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getHealthIcon = (score) => {
    if (score >= 80) return <CheckCircle className="h-5 w-5" />;
    if (score >= 60) return <AlertCircle className="h-5 w-5" />;
    return <AlertCircle className="h-5 w-5" />;
  };

  const stats = [
    <ViewLayout.Stat 
      key="total-props"
      title="Total Props" 
      value={systemStats.totalProps} 
      icon={Package} 
      color="blue" 
    />,
    <ViewLayout.Stat 
      key="available-props"
      title="Available" 
      value={systemStats.availableProps} 
      icon={CheckCircle} 
      color="green" 
    />,
    <ViewLayout.Stat 
      key="checked-out-props"
      title="Checked Out" 
      value={systemStats.checkedOutProps} 
      icon={Clock} 
      color="orange" 
    />,
    <ViewLayout.Stat 
      key="organizations"
      title="Organizations" 
      value={systemStats.totalOrganizations} 
      icon={Building} 
      color="purple" 
    />
  ];

  return (
    <ViewLayout
      title="Database Dashboard"
      subtitle="System Overview & Analytics"
      stats={stats}
    >
      <div className="space-y-6">
        {/* System Health Score */}
        <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" />
          System Health Score
        </h3>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${getHealthColor(healthScore)}`}>
            {getHealthIcon(healthScore)}
            <span className="font-bold text-2xl">{Math.round(healthScore)}%</span>
          </div>
          <div className="flex-1">
            <div className="text-sm text-gray-600 mb-1">Overall System Performance</div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  healthScore >= 80 ? 'bg-green-500' : 
                  healthScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${healthScore}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Database Status */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            Database Connection & Status
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Connection Status */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Connection Status</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Database Connection:</span>
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Connected
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">API Status:</span>
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Active
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Real-time Sync:</span>
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Enabled
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Last Sync:</span>
                  <span className="text-sm text-gray-600">
                    {new Date().toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Data Counts */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Data Overview</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Props Loaded:</span>
                  <span className="font-medium text-green-600">{systemStats.totalProps}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Organizations:</span>
                  <span className="font-medium text-green-600">{systemStats.totalOrganizations}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Transfer Requests:</span>
                  <span className="font-medium text-green-600">{transferRequests.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">User Lists:</span>
                  <span className="font-medium text-green-600">{systemStats.totalLists}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Active Reservations:</span>
                  <span className="font-medium text-green-600">{reservations.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wasabi Storage Management */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Database className="h-5 w-5 text-purple-600" />
            Wasabi Storage Management
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Storage Status */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Storage Overview</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Total Props with Images:</span>
                  <span className="font-medium text-blue-600">
                    {props.filter(p => p.images && p.images.length > 0).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Wasabi Cloud Images:</span>
                  <span className="font-medium text-green-600">
                    {props.filter(p => p.images && p.images.some(img => img.startsWith('http'))).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Local File References:</span>
                  <span className="font-medium text-orange-600">
                    {props.filter(p => p.images && p.images.some(img => !img.startsWith('http'))).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Props without Images:</span>
                  <span className="font-medium text-gray-600">
                    {props.filter(p => !p.images || p.images.length === 0).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">URLs Expiring Soon:</span>
                  <span className="font-medium text-yellow-600">
                    {(() => {
                      // Count URLs that expire within 7 days
                      const expiringUrls = props.filter(p => 
                        p.images && p.images.some(img => {
                          if (!img.startsWith('http') || !img.includes('Expires=')) return false;
                          try {
                            const expiresMatch = img.match(/Expires=(\d+)/);
                            if (!expiresMatch) return false;
                            const expiresTimestamp = parseInt(expiresMatch[1]) * 1000;
                            const sevenDaysFromNow = Date.now() + (7 * 24 * 60 * 60 * 1000);
                            return expiresTimestamp < sevenDaysFromNow;
                          } catch {
                            return false;
                          }
                        })
                      ).length;
                      return expiringUrls;
                    })()} props
                  </span>
                </div>
              </div>
            </div>

            {/* Storage Actions */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Storage Tools</h4>
              <div className="space-y-3">
                <button 
                  onClick={() => setActiveView('wasabi-cleanup')}
                  className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex-shrink-0">
                    <Database className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-medium">Cleanup Orphaned Files</div>
                    <div className="text-sm text-gray-600">Find and remove unused images from Wasabi storage</div>
                  </div>
                </button>
                
                <button 
                  onClick={() => setActiveView('wasabi-migration')}
                  className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex-shrink-0">
                    <Package className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium">Migrate Local Images</div>
                    <div className="text-sm text-gray-600">Upload remaining local images to Wasabi cloud</div>
                  </div>
                </button>
                
                <button 
                  onClick={() => setActiveView('wasabi-renewal')}
                  className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex-shrink-0">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-medium">Renew Expired URLs</div>
                    <div className="text-sm text-gray-600">Refresh expiring signed URLs for continued access</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              System Activity
            </h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Pending Reservations:</span>
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
                  {systemStats.pendingReservations}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Active Transfers:</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  {systemStats.activeTransfers}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Reserved Props:</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                  {systemStats.reservedProps}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Utilization Rate:</span>
                <span className="text-sm font-medium">
                  {systemStats.totalProps > 0 ? 
                    Math.round((systemStats.checkedOutProps / systemStats.totalProps) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* System Performance */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <Server className="h-5 w-5 text-blue-600" />
              Performance Metrics
            </h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Availability Rate:</span>
                <span className="text-sm font-medium">
                  {systemStats.totalProps > 0 ? 
                    Math.round((systemStats.availableProps / systemStats.totalProps) * 100) : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Network Coverage:</span>
                <span className="text-sm font-medium">{systemStats.totalOrganizations} organizations</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">User Engagement:</span>
                <span className="text-sm font-medium">{systemStats.totalLists} active lists</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Response Time:</span>
                <span className="text-sm font-medium text-green-600">&lt; 200ms</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions for Service Providers */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            Service Provider Tools
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <ArrowRightLeft className="h-6 w-6 text-blue-600" />
              <div className="text-left">
                <div className="font-medium">Manage Transfers</div>
                <div className="text-sm text-gray-600">Coordinate prop transfers</div>
              </div>
            </button>
            
            <button className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Database className="h-6 w-6 text-green-600" />
              <div className="text-left">
                <div className="font-medium">Data Export</div>
                <div className="text-sm text-gray-600">Export system data</div>
              </div>
            </button>
            
            <button className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <TrendingUp className="h-6 w-6 text-purple-600" />
              <div className="text-left">
                <div className="font-medium">Analytics</div>
                <div className="text-sm text-gray-600">View detailed reports</div>
              </div>
            </button>
          </div>
        </div>
      </div>
      </div>
    </ViewLayout>
  );
};

export default DatabaseDashboardView;
